import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Returns a ref whose `.current` is always the latest `value`.
 *
 * Use this to read the freshest value inside callbacks/effects without
 * adding the value to a dep array.
 *
 * ── Why this exists (JWT rotation hazard) ────────────────────────────────
 *
 * CRITICAL: don't put `session`, `session.access_token`, `accessToken`, or
 * `jwt` directly in `useEffect` / `useCallback` dep arrays. Supabase
 * auto-rotates the access JWT every ~58min (1h TTL); rotation re-fires
 * every effect that depends on it, and re-creates every callback's
 * identity. The 2026-04-28 Plaid quota incident traced back to exactly
 * this pattern in `usePlaidSync` — one user with the app open burned 120
 * Plaid `/accounts/get` calls in 24h. See PR #582 / #584 and the comment
 * blocks in `usePlaidSync/index.js` and `useMcpKey.js`.
 *
 * Pattern to use instead:
 *   const tokenRef = useLatestRef(token);
 *   // ... later, inside an effect/callback:
 *   const { data } = await fetch(url, {
 *     headers: { Authorization: `Bearer ${tokenRef.current}` },
 *   });
 *
 * Then gate the work-doing effect on a *stable* boolean
 * (e.g. `!!session`, `!!userId`) so it fires once on sign-in / sign-out,
 * not on every rotation.
 *
 * @template T
 * @param {T} value
 * @returns {{ current: T }}
 */
export function useLatestRef(value) {
  const ref = useRef(value);
  // Updated in the layout phase, with the DOM (#2188): a passive effect can
  // run after the user (or a toast's Undo) already acted on the new render,
  // and the callback then reads the previous render's props — Undo of a
  // deleted bill composed with the pre-delete budget and took its amount twice.
  useLayoutEffect(() => { ref.current = value; }, [value]);
  return ref;
}

/**
 * A stable function that calls the latest `fn` (#2145).
 *
 * For a callback that outlives the render that made it, such as a toast's Undo
 * or a handler a long-lived object keeps: the function from the render that
 * raised the toast closes over that render's props and state, so what it writes
 * composes with a stale picture. This one reads the ref when it is CALLED, never
 * during render, so it can be passed to children and `.map` freely (the
 * `react-hooks/refs` rule sees a plain hook result, not a ref).
 *
 * @template {(...args: any[]) => any} F
 * @param {F | null | undefined} fn
 * @returns {(...args: Parameters<F>) => ReturnType<F>}
 */
export function useLatestCallback(fn) {
  const ref = useLatestRef(fn);
  return useCallback((...args) => ref.current(...args), [ref]);
}
